/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
import React, { useCallback, useEffect, useRef } from 'react';
import { isDesktop } from 'react-device-detect';
import { showInitMessage } from './helpers';
import {
  IGamecastStreamSession,
  IGameCast,
  IGameCastArgs,
  IGamecastServerDisconnectReason,
  IGamecastVideoStats,
} from './interfaces';
import GameCastService from '../../../../../services/GameCastService';
import {
  GAME_CAST_REQUEST_TIMEOUT,
  enableDebug,
  disallowKeyCode,
} from './constants';
import { IInitMessage } from '../Pureweb/interfaces';
import { StreamingProviders } from '../../../providers/MytaverseEventProvider/interfaces';
import { IParticipant } from '../../../../../interfaces/participants';
import { RegisterTouchToMouse } from './touchtomouse';
import { useMytaverse } from '../../../../../providers/MytaverseProvider';
import { useChatState } from '../../../../../hooks/context';
import { IRoom } from '../../../../../interfaces/rooms';
import { IStartStreamSessionOutput } from '../../../../../interfaces/gamecast';

export const useGameInitialization = ({
  streamId,
  setIsGamecastCrash,
  gamecastStats,
  currentRoom,
  sessionKey
}: {
  browserType: string;
  streamId: string | null;
  currentParticipant: IParticipant | undefined;
  setStreamingProvider: React.Dispatch<
    React.SetStateAction<StreamingProviders | null>
  >;
  setIsGamecastCrash: React.Dispatch<React.SetStateAction<boolean>>;
  gamecastStats: React.MutableRefObject<IGamecastVideoStats | null>;
  currentRoom: IRoom | null | undefined;
  sessionKey?:string;
}) => {
  const { sendMessageToUnreal, navigateToEventsPage, user } = useMytaverse();
  const { setOpen: setOpenChat } = useChatState();

  const gamecastClientRef = useRef<null | IGameCast>(null);
  const gamecastRTCStatsIntervalRef = useRef<any>();
  const streamStartedConnectionRef = useRef<boolean>(false);
  const abortConnectionRef = useRef<boolean>(false);

  //@ts-ignore
  const GamecastClient = window.gamecastsdk.GameCast;
  let connectedTime = 0;

  if (enableDebug) {
    //@ts-ignore
    window.gamecastsdk.setLogLevel('debug');
  }

  const closeGamecastStream = () => {
    abortConnectionRef.current = true;

    if (gamecastClientRef.current) {
      console.log(`Closing gamecast stream`);
      gamecastClientRef.current.close();
      gamecastClientRef.current = null;
    }

    if (gamecastRTCStatsIntervalRef.current) {
      clearInterval(gamecastRTCStatsIntervalRef.current);
      gamecastRTCStatsIntervalRef.current = null;
    }
  };

  const abortGamecastConnection = () => {
    if (!streamStartedConnectionRef.current) {
      console.log(`Aborting gamecast connection`);
    }
    abortConnectionRef.current = true;
    closeGamecastStream();
  };

  const getGameCastClient = (
    videoElement: HTMLVideoElement,
    isReconnect?: boolean,
  ) => {
    const gamecastClient: IGameCast = new GamecastClient({
      videoElement,
      inputConfiguration: {
        autoMouse: true,
        autoKeyboard: true,
        autoGamepad: true,
        resetOnDetach: true,
        hapticFeedback: true,
        setCursor: 'visibility',
        autoPointerLock: 'fullscreen',
        keyboardFilter: (event) => {
          if (enableDebug) {
            console.warn('gamecast keyboard event', { event });
          }

          if (
            user?.roles?.length &&
            user.roles.indexOf('SUPER_ADMIN') === -1 &&
            disallowKeyCode.indexOf(event.code) > -1
          ) {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          if (event.metaKey) {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          return true;
        },
      },
      clientConnection: {
        connectionState: (state) => {
          console.log(`Gamecast connection state: ${state}`);

          if (state === 'connected') {
            connectedTime = Date.now();
          }
        },
        channelError: (state) => {
          console.log(`Gamecast channel error: ${state}`);
        },
        serverDisconnect: async (state: IGamecastServerDisconnectReason) => {
          switch (state) {
            case IGamecastServerDisconnectReason.Terminated:
              console.log(`Gamecast server terminated: ${state}`);
              gamecastClient.close();
              setIsGamecastCrash(true);

              if (isReconnect) {
                setOpenChat(false);
              }
              break;
            default:
              console.log(`Gamecast server disconnect: ${state}`);
              break;
          }
        },
        applicationMessage: (state) => {
          console.log(`Gamecast application message: ${state}`);
        },
      },
    } as IGameCastArgs);

    return gamecastClient;
  };

  const initializeGamecastStream = async (
    participantId = '',
    websocketSessionId: string,
    initMessageHandler: () => Promise<IInitMessage | null>,
    region: string,
    launchArgs: string[] | null,
  ) => {
    let isStarted = false;

    if (!streamId) {
      return { isStarted };
    }

    try {
      const gamecastStreamSession = await createGamecastStreamSession(
        participantId,
        websocketSessionId,
        GAME_CAST_REQUEST_TIMEOUT,
        streamId,
        initMessageHandler,
        region,
        sendMessageToUnreal,
        launchArgs,
        sessionKey
      );
    } catch (e) {
      console.log(e);
    }

    return {
      isAborted: abortConnectionRef.current,
      gamecastRTCStatsInterval: gamecastRTCStatsIntervalRef.current,
    };
  };

  const processActiveStreamSession = async (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => {
    let isStarted = false;

    if (
      gamecastStreamSession &&
      !abortConnectionRef.current &&
      gamecastClientRef.current
    ) {
      console.log(`Start processSignalResponse`);

      await gamecastClientRef.current.processSignalResponse(
        gamecastStreamSession?.SignalResponse as string,
      );
      gamecastClientRef.current.attachInput();
      isStarted = true;
    }

    if (abortConnectionRef.current) {
      abortGamecastConnection();
      isStarted = false;
    }

    streamStartedConnectionRef.current = isStarted;

    return { isStarted, isAborted: abortConnectionRef.current };
  };

  const createGamecastStreamSession = async (
    userId: string,
    websocketSessionId: string,
    timeout: number,
    streamId: string,
    initMessageHandler: () => Promise<IInitMessage | null>,
    region: string,
    sendMessageToUnreal: (message: any) => void,
    launchArgs: string[] | null,
    sessionKey?: string
  ): Promise<IGamecastStreamSession | null> => {
    const gamecastStreamSession = await waitGamecastStreamSession(
      userId as string,
      websocketSessionId as string,
      timeout,
      streamId,
      initMessageHandler,
      region,
      launchArgs,
      sessionKey
    );

    if (!gamecastStreamSession) {
      navigateToEventsPage('notifications.noAvailableStreamingProviders');
      return null;
    }

    console.log('Gamecast stream session', {
      arn: gamecastStreamSession.Arn,
      applicationArn: gamecastStreamSession.ApplicationArn,
      userId: gamecastStreamSession.UserId,
      streamGroupId: gamecastStreamSession.StreamGroupId,
    });

    sendMessageToUnreal({
      action: 'CLIENT_CONNECTION_DATA',
      streamingProvider: 'GAMECAST',
      sessionArn: gamecastStreamSession.Arn,
      region,
    });

    return gamecastStreamSession;
  };

  const waitGamecastStreamSession = (
    participantId: string,
    websocketSessionId: string,
    timeout: number,
    streamId: string,
    initMessageHandler: () => Promise<IInitMessage | null>,
    region: string,
    launchArgs: string[] | null,
    sessionKey?: string
  ): Promise<IGamecastStreamSession | null> => {
    return new Promise<IGamecastStreamSession>(async (resolve, reject) => {
      const maxRetriesToStartGamecastPlayerSession = 10;
      let totalRetriesToStartGamecastPlayerSession = 0;
      let lastStatTime = 0;
      let lastStatBytesReceived = 0;
      let statSamples: {
        downstreamTransferSpeed: string;
        downstreamConnectionSpeed: number;
        secondsElapsed: number;
      }[] = [];

      const description = 'Gamecast streaming session';
      const gameName = streamId;
      const videoElement = document.getElementById('gamecastVideoTag') as any;

      const gamecast: IGameCast = getGameCastClient(videoElement, true);

      gamecastClientRef.current = gamecast;

      gamecastRTCStatsIntervalRef.current = setInterval(async () => {
        const stats = await gamecast.getRTCStats();

        for (const id of stats.keys()) {
          const stat = stats.get(id);

          if (stat.type === 'inbound-rtp') {
            // webrtc stats derived speed
            const webrtcStatsSpeed = {
              transferSpeed: 0,
              connectionSpeed: 0,
              avgTransferSpeed: 0,
              avgConnectionSpeed: 0,
            };
            if (lastStatTime) {
              const mbytesReceived = (
                (stat.bytesReceived - lastStatBytesReceived) /
                Math.pow(1024, 2)
              ).toFixed(2);
              const secondsElapsed = (Date.now() - lastStatTime) / 1000;
              const bitrate = (8 * +mbytesReceived) / secondsElapsed;

              if (statSamples.length > 450) {
                statSamples.shift();
              }
              statSamples.push({
                downstreamTransferSpeed: mbytesReceived,
                downstreamConnectionSpeed: +mbytesReceived * 8,
                secondsElapsed,
              });
              const avgTransferSpeed =
                statSamples.reduce(
                  (acc, cur) => acc + +cur.downstreamTransferSpeed,
                  0,
                ) / statSamples.length;
              const avgBitrate =
                statSamples.reduce(
                  (acc, cur) =>
                    acc +
                    (8 * +cur.downstreamTransferSpeed) / cur.secondsElapsed,
                  0,
                ) / statSamples.length;
              if (enableDebug) {
                console.warn(
                  `current transfer speed: ${mbytesReceived}MBs\n server connection speed: ${bitrate.toFixed(
                    2,
                  )} MBPs\n avg transfer speed: ${avgTransferSpeed.toFixed(
                    2,
                  )} MBs over ${
                    statSamples.length
                  } samples\n avg server connection speed ${avgBitrate.toFixed(
                    2,
                  )} MBPs`,
                  {
                    stat,
                    mbytesReceived,
                    secondsElapsed,
                    bytesrec: stat.bytesReceived,
                    lastStatBytesReceived,
                    bitrate,
                  },
                );
              }
              webrtcStatsSpeed.transferSpeed = +mbytesReceived;
              webrtcStatsSpeed.connectionSpeed = +bitrate.toFixed(2);
              webrtcStatsSpeed.avgTransferSpeed = +avgTransferSpeed.toFixed(2);
              webrtcStatsSpeed.avgConnectionSpeed = +avgBitrate.toFixed(2);
            }
            lastStatTime = stat.timestamp;
            lastStatBytesReceived = stat.bytesReceived;
            gamecastStats.current = {
              ...stat,
              ...webrtcStatsSpeed,
            };
          }
        }
      }, 2000);

      RegisterTouchToMouse(videoElement as any);

      const gamecastSignalData = await gamecast.generateSignalRequest();

      console.log(`Starting gamecast player session`);

      const startGamecastPlayerSession = async (resolve: any) => {
        try {
          const initMessage = await initMessageHandler();
          if (!initMessage) {
            console.log({ initMessage });
            return;
          }
          showInitMessage(JSON.stringify(initMessage));

          totalRetriesToStartGamecastPlayerSession += 1;

          if (abortConnectionRef.current) {
            return resolve(null);
          }

          await GameCastService.startGameSession({
            description,
            gameName,
            participantId,
            signalRequest: gamecastSignalData,
            timeout,
            streamId,
            initMessage,
            region,
            websocketSessionId,
            launchArgs,
            sessionKey
          });

        } catch (e) {
          console.log(`Couldn't start gamecast session`);
        }
      };
      startGamecastPlayerSession(resolve);
    });
  };

  const handleReconnectGamecast = useCallback(
    async ({
      participantId,
      websocketSessionId,
      initMessageHandler,
      region,
      launchArgs,
    }: {
      participantId: string;
      websocketSessionId: string;
      initMessageHandler: () => Promise<IInitMessage | null>;
      region: string;
      launchArgs: string[] | null;
    }) => {
      if (!participantId || !streamId) {
        return;
      }

      const signalResponse = await createGamecastStreamSession(
        participantId,
        websocketSessionId,
        GAME_CAST_REQUEST_TIMEOUT,
        streamId,
        initMessageHandler,
        region,
        sendMessageToUnreal,
        launchArgs,
      );

      // if (signalResponse) {
      //   await gamecast.processSignalResponse(signalResponse.SignalResponse);
      //   gamecast.attachInput();
      // }
    },
    [streamId],
  );

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    const handleBlur = () => {
      if (!currentRoom || !gamecastClientRef.current) {
        return;
      }

      try {
        gamecastClientRef.current?.detachInput();
      } catch (e) {
        console.error(e);
      }
    };

    const handleFocus = () => {
      if (!currentRoom || !gamecastClientRef.current) {
        return;
      }

      try {
        gamecastClientRef.current?.attachInput();
      } catch (e) {
        console.error(e);
      }
    };

    if (gamecastClientRef.current) {
      window.addEventListener('blur', handleBlur);
      window.addEventListener('focus', handleFocus);
    }

    return () => {
      if (gamecastClientRef.current) {
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('focus', handleFocus);
      }
    };
  }, [gamecastClientRef.current, currentRoom]);

  return {
    initializeGamecastStream,
    closeGamecastStream,
    abortGamecastConnection,
    handleReconnectGamecast,
    gamecastClientRef,
    processActiveStreamSession,
  };
};
